/**
 * SessionDetailsSection
 *
 * Modality, Place of Service, and — in the note view — the DOS-split date/time
 * model (GDN-2510):
 *
 *   - **Date of Service** — its own stable, always-visible field; the billing
 *     date on the claim (CMS-1500 Box 24A). Writes only `date_of_service`.
 *   - **Session time** — the official time selector (in room / scheduled /
 *     set manually). Governs the time of day only; writes
 *     `start_time`/`end_time`/`time_source`, never the DOS and never
 *     `scheduled_for` (the calendar slot stays untouched).
 *
 * The selector's manual option carries start + duration, so the note view has
 * no standalone Duration row; the encounter view (no time fields) keeps it.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { PlaceOfServiceSelect } from '@features/billing/components/Claim/PlaceOfServiceSelect';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { getModalityPlaceOfServiceError } from '@utils/modalityPlaceOfService';

import type { Modality } from '../../types/attestations';
import { OfficialTimeSelector } from '../OfficialTimeSelector';

import type { SessionOfficialTime } from './types';

const DURATION_PRESETS = [10, 20, 30, 60];

interface SessionDetailsSectionProps {
  modality: Modality | null;
  onModalityChange: (modality: Modality) => void;
  placeOfService: string | null;
  onPlaceOfServiceChange: (pos: string | null) => void;
  duration: number | null;
  onDurationChange: (minutes: number) => void;
  editable?: boolean;
  /** Canonical service date (date_of_service), date-only "YYYY-MM-DD". Defaults
   * the Date of Service control; independent of the session time. */
  dateOfService?: string | null;
  /** Note view only — writes the billing day (`date_of_service`) and nothing else. */
  onTimeChange?: (updates: {
    /** Date-only "YYYY-MM-DD" billing day — writes date_of_service only. */
    dateOfService?: string;
  }) => void;
  /** Note view only — the official session-time selector context (GDN-2510). */
  officialTime?: SessionOfficialTime;
}

export const SessionDetailsSection: React.FC<SessionDetailsSectionProps> = ({
  modality,
  onModalityChange,
  placeOfService,
  onPlaceOfServiceChange,
  duration,
  onDurationChange,
  editable = true,
  dateOfService,
  onTimeChange,
  officialTime,
}) => {
  const showDateOfService = onTimeChange !== undefined;

  // Local state for custom duration input
  const [customDuration, setCustomDuration] = useState('');
  const isPreset = duration !== null && DURATION_PRESETS.includes(duration);
  const [isCustomMode, setIsCustomMode] = useState(!isPreset && duration !== null);

  // Local state for the Date of Service (billing day), independent of the clock.
  const [localDos, setLocalDos] = useState('');
  const [attemptedBillingError, setAttemptedBillingError] = useState<string | null>(null);
  const currentBillingError = getModalityPlaceOfServiceError({ modality, placeOfService });
  const billingError = attemptedBillingError ?? currentBillingError;

  useEffect(() => {
    if (!currentBillingError) {
      setAttemptedBillingError(null);
    }
  }, [currentBillingError]);

  // Initialize the Date of Service from the canonical service date. It is a
  // date-only value, so anchor it to local midnight to avoid a UTC day-shift.
  useEffect(() => {
    if (dateOfService) {
      const d = new Date(`${dateOfService}T00:00:00`);
      if (!isNaN(d.getTime())) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        setLocalDos(`${y}-${m}-${day}`);
      }
    }
  }, [dateOfService]);

  // Date of Service edits are independent — they write only date_of_service
  // and never touch the session time. Commit on blur.
  const handleDosBlur = useCallback(() => {
    if (!localDos) return;
    onTimeChange?.({ dateOfService: localDos });
  }, [localDos, onTimeChange]);

  const handleModalitySelect = useCallback(
    (value: Modality) => {
      setAttemptedBillingError(null);
      onModalityChange(value);
    },
    [onModalityChange],
  );

  const handlePlaceOfServiceSelect = useCallback(
    (value: string) => {
      const nextPlaceOfService = value || null;
      const error = getModalityPlaceOfServiceError({
        modality,
        placeOfService: nextPlaceOfService,
      });
      if (error) {
        setAttemptedBillingError(error);
        return;
      }

      setAttemptedBillingError(null);
      onPlaceOfServiceChange(nextPlaceOfService);
    },
    [modality, onPlaceOfServiceChange],
  );

  return (
    <Stack spacing={2}>
      {/* Modality */}
      <FormControl fullWidth size="small" disabled={!editable}>
        <InputLabel>Modality</InputLabel>
        <Select
          value={modality ?? ''}
          onChange={e => handleModalitySelect(e.target.value)}
          label="Modality"
        >
          <MenuItem value="in_person">In-person</MenuItem>
          <MenuItem value="video">Video</MenuItem>
          <MenuItem value="audio">Audio</MenuItem>
        </Select>
      </FormControl>

      {/* Place of Service */}
      <PlaceOfServiceSelect
        value={placeOfService ?? ''}
        onChange={e => handlePlaceOfServiceSelect(e.target.value)}
        label="Place of Service"
        error={!!billingError}
        helperText={billingError}
        disabled={!editable}
        sx={{
          width: '100%',
          minWidth: '100%',
          maxWidth: '100%',
          '& .MuiInputBase-input': {
            padding: '8.5px 14px',
            paddingRight: '32px',
            fontSize: '0.875rem',
            textAlign: 'left',
          },
          '& .MuiOutlinedInput-root': {
            height: 'auto',
          },
        }}
      />

      {/* Date of Service — the billing day (CMS-1500 Box 24A). Independent of
          the session time below. */}
      {showDateOfService && (
        <TextField
          type="date"
          label="Date of Service"
          value={localDos}
          onChange={e => setLocalDos(e.target.value)}
          onBlur={handleDosBlur}
          fullWidth
          size="small"
          disabled={!editable || officialTime?.locked}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText="The billing date on the claim."
        />
      )}

      {/* Session time — the official time selector (GDN-2510). Time of day
          only; the Date of Service field above owns the billing date. */}
      {officialTime && (
        <OfficialTimeSelector
          variant="inline"
          encounter={officialTime.encounter}
          encounterId={officialTime.encounterId}
          isDictation={officialTime.isDictation}
          onCommit={officialTime.onCommit}
          recordingWindow={officialTime.recordingWindow}
          disabled={!editable}
          locked={officialTime.locked}
        />
      )}

      {/* Duration — encounter view only; in the note view the selector's
          manual option carries start + duration. */}
      {!officialTime && (
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
            Duration (minutes)
          </Typography>
          <ButtonGroup size="small" fullWidth disabled={!editable} sx={{ mb: isCustomMode ? 1 : 0 }}>
            {DURATION_PRESETS.map(preset => (
              <Button
                key={preset}
                variant={duration === preset && !isCustomMode ? 'contained' : 'outlined'}
                onClick={() => {
                  onDurationChange(preset);
                  setCustomDuration('');
                  setIsCustomMode(false);
                }}
              >
                {preset}
              </Button>
            ))}
            <Tooltip title="Custom duration">
              <Button
                variant={isCustomMode ? 'contained' : 'outlined'}
                onClick={() => setIsCustomMode(true)}
                sx={{ minWidth: 36, px: 0 }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
          </ButtonGroup>
          {isCustomMode && (
            <TextField
              type="number"
              size="small"
              fullWidth
              placeholder="Enter minutes"
              value={
                customDuration ||
                (duration && !DURATION_PRESETS.includes(duration) ? String(duration) : '')
              }
              onChange={e => {
                setCustomDuration(e.target.value);
                const val = parseInt(e.target.value, 10);
                if (val > 0) onDurationChange(val);
              }}
              disabled={!editable}
              slotProps={{ htmlInput: { min: 1 } }}
            />
          )}
        </Box>
      )}
    </Stack>
  );
};
